import { supabase } from '../lib/supabase';

/**
 * Newsletter Subscription Service
 * Handles newsletter subscription operations
 */

/**
 * Subscribe to newsletter
 * @param {Object} subscriptionData - Subscription data
 * @param {string} subscriptionData.fullName - Subscriber's full name
 * @param {string} subscriptionData.email - Subscriber's email
 * @param {string} subscriptionData.company - Subscriber's company
 * @returns {Promise<Object>} Subscription result
 */
export const subscribeToNewsletter = async ({ fullName, email, company }) => {
  try {
    const { data, error } = await supabase?.from('newsletter_subscriptions')?.insert({
        full_name: fullName,
        email: email?.toLowerCase()?.trim(),
        company: company?.trim(),
        is_active: true
      })?.select()?.single();

    if (error) {
      // Check if it's a duplicate email error
      if (error?.code === '23505') {
        throw new Error('Cet email est déjà inscrit à notre newsletter.');
      }
      throw error;
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error subscribing to newsletter:', error);
    throw error;
  }
};

/**
 * Unsubscribe from newsletter
 * @param {string} email - Subscriber's email
 * @returns {Promise<Object>} Unsubscribe result
 */
export const unsubscribeFromNewsletter = async (email) => {
  try {
    const { data, error } = await supabase?.from('newsletter_subscriptions')?.update({
        is_active: false,
        unsubscribed_at: new Date()?.toISOString()
      })?.eq('email', email?.toLowerCase()?.trim())?.select()?.single();

    if (error) {
      throw error;
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error unsubscribing from newsletter:', error);
    throw error;
  }
};

/**
 * Get all newsletter subscriptions (admin only)
 * @returns {Promise<Array>} List of subscriptions
 */
export const getAllSubscriptions = async () => {
  try {
    const { data, error } = await supabase?.from('newsletter_subscriptions')?.select('*')?.order('subscribed_at', { ascending: false });

    if (error) {
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching subscriptions:', error);
    throw error;
  }
};

/**
 * Get active newsletter subscriptions count (admin only)
 * @returns {Promise<number>} Count of active subscriptions
 */
export const getActiveSubscriptionsCount = async () => {
  try {
    const { count, error } = await supabase?.from('newsletter_subscriptions')?.select('*', { count: 'exact', head: true })?.eq('is_active', true);

    if (error) {
      throw error;
    }

    return count || 0;
  } catch (error) {
    console.error('Error fetching subscriptions count:', error);
    throw error;
  }
};